import jsPDF from "jspdf";
import "jspdf-autotable";

const printFloorAgendaPDF = (selectedAgenda) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  const leftMargin = 30;
  const rightMargin = 30;
  const topMargin = 12;
  const bottomMargin = 30;

  const addHeader = (doc, pageNumber, totalPages) => {
    const headerHeight = 15;
    const headerColor =
      selectedAgenda.chamber === "SENATE"
        ? [138, 95, 104]
        : selectedAgenda.chamber === "HOUSE"
        ? [103, 137, 127] 
        : [103, 137, 127]; 

    const contentWidth = doc.internal.pageSize.width - leftMargin - rightMargin;

    doc.setFillColor(...headerColor);
    doc.rect(leftMargin, topMargin, contentWidth, headerHeight, "F");

    doc.setFont("helvetica", "normal");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);

    const agendaText = `2025 Montana Legislature - 69th Regular Session  Printed: ${new Date().toLocaleDateString(
      "en-US",
      {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      }
    )} ${new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}   Page ${pageNumber} of ${totalPages}`;

    const agendaTextWidth = doc.getTextWidth(agendaText);
    const centerX = leftMargin + contentWidth / 2;
    doc.text(agendaText, centerX - agendaTextWidth / 2, topMargin + 12);
  };

  const paddingAfterHeader = 70; 
  const headingLineSpacing = 15;

  const agendaText = `${selectedAgenda.chamber} AGENDA #${selectedAgenda.agendaNumber}`;
  const dayText = `LEGISLATIVE DAY ${selectedAgenda?.legislativeCalendarDay?.calendarDay}`;
  const dateText = selectedAgenda?.formattedAgendaDate
    ? `${new Date(selectedAgenda.formattedAgendaDate).toLocaleDateString(
        "en-US",
        {
          year: "numeric",
          month: "short",
          day: "numeric",
        }
      )}, ${
        selectedAgenda?.conveneTime
          ? new Date(selectedAgenda.conveneTime).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
          : ""
      }`
    : "";

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(14);

  const agendaTextWidth = doc.getTextWidth(agendaText);
  doc.text(
    agendaText,
    (doc.internal.pageSize.width - agendaTextWidth) / 2,
    topMargin + paddingAfterHeader
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);

  const dayTextWidth = doc.getTextWidth(dayText);
  doc.text(
    dayText,
    (doc.internal.pageSize.width - dayTextWidth) / 2,
    topMargin + paddingAfterHeader + headingLineSpacing
  );

  const dateTextWidth = doc.getTextWidth(dateText);
  doc.text(
    dateText,
    (doc.internal.pageSize.width - dateTextWidth) / 2,
    topMargin + paddingAfterHeader + headingLineSpacing * 2
  );

  let paddingBeforeTable =
    topMargin + paddingAfterHeader + headingLineSpacing * 4 + 15;

  const processVotes = (billVoteDetails) => {
    let totalYesVotes = 0;
    let totalNoVotes = 0;
    let committeeName = "Not available";
    let latestVoteDate = new Date(0); // Initialize to the earliest possible date

    const getLatestConferenceVote = (executiveActionData) => {
      let latestVote = null;

      executiveActionData.forEach(voteDetail => {
        const committeeTypeDescription = voteDetail.standingCommitteeMeeting?.standingCommittee?.committeeDetails?.committeeCode?.committeeType?.description;

        if (committeeTypeDescription && committeeTypeDescription.toLowerCase().includes("conference")) {
          const voteTime = new Date(voteDetail.voteTime);

          if (!latestVote || voteTime > new Date(latestVote.voteTime)) {
            latestVote = voteDetail;
          }
        }
      });

      return latestVote;
    };

    if (billVoteDetails && billVoteDetails.executiveActionData) {
      const latestConferenceVote = getLatestConferenceVote(billVoteDetails.executiveActionData);

      if (latestConferenceVote) {
        // Process the latest conference vote if conference vote exists...we need to give it priority without matching the chamber. 
        totalYesVotes = latestConferenceVote.legislatorVotes.filter(
          (vote) => vote.committeeVote === "YES" || vote.committeeVote === "YES_BY_PROXY"
        ).length;

        totalNoVotes = latestConferenceVote.legislatorVotes.filter(
          (vote) => vote.committeeVote === "NO" || vote.committeeVote === "NO_BY_PROXY"
        ).length;

        committeeName = latestConferenceVote.standingCommitteeMeeting.standingCommittee.committeeDetails?.committeeCode?.name || "Not available";
      } else {
        // Fallback to normal logic if no conference vote is found
        for (let i = billVoteDetails.executiveActionData.length - 1; i >= 0; i--) {
          const voteDetail = billVoteDetails.executiveActionData[i];
          const voteDate = new Date(voteDetail.voteTime);

          if (voteDate > latestVoteDate && voteDetail.standingCommitteeMeeting?.standingCommittee?.chamber === selectedAgenda.chamber) {
            latestVoteDate = voteDate;
            totalYesVotes = voteDetail.legislatorVotes.filter(
              (vote) => vote.committeeVote === "YES" || vote.committeeVote === "YES_BY_PROXY"
            ).length;

            totalNoVotes = voteDetail.legislatorVotes.filter(
              (vote) => vote.committeeVote === "NO" || vote.committeeVote === "NO_BY_PROXY"
            ).length;

            committeeName = voteDetail.standingCommitteeMeeting.standingCommittee.committeeDetails?.committeeCode?.name || "Not available";
          }
        }
      }
    }

    return `${committeeName}: Vote: ${totalYesVotes}-${totalNoVotes}`;
  };

  const groupedBills = Object.entries(
    selectedAgenda.agendaBills.reduce((groups, item) => {
      const description = item.orderOfBusiness?.description || "Other";
      if (!groups[description]) {
        groups[description] = [];
      }
      groups[description].push(item);
      return groups;
    }, {})
  ).sort(([descA], [descB]) => {
    const order = {
      "First Reading of Bills": 1,
      "Second Reading of Bills": 2,
      "Third Reading of Bills": 3,
      Other: 4,
    };
    return (order[descA] || 999) - (order[descB] || 999);
  });

  groupedBills.forEach((  [description, bills]) => {
    const orderOfBusinessText = `Order of Business #${bills[0]?.orderOfBusiness?.order} -- ${description}`;

    const spaceForTextAndTable = 80;
    const currentY = doc.lastAutoTable
      ? doc.lastAutoTable.finalY
      : paddingBeforeTable;

    if (
      paddingBeforeTable + spaceForTextAndTable >
      doc.internal.pageSize.height - bottomMargin
    ) {
      doc.addPage();
      paddingBeforeTable = topMargin + 30;
    }

    doc.setFontSize(10);
    doc.text(orderOfBusinessText, leftMargin, paddingBeforeTable);
    paddingBeforeTable += 15;

    const tableData = bills.sort((a, b) => a.billOrderNumber - b.billOrderNumber).map((bill, index) => {
      // Sponsor name
      const sponsor = bill?.sponsor
      ? `Sponsor:\n${bill.sponsor.lastName}, ${bill.sponsor.firstName?.charAt(0)}.`
      : "";

      // Carrier logic (only show if bill is from opposite chamber)
      const showCarrier =
        bill?.billDetails?.carrierInfo &&
        ((selectedAgenda.chamber === "SENATE" && ["HB", "HJ", "HR"].includes(bill?.billDetails?.billType?.code)) ||
         (selectedAgenda.chamber === "HOUSE" && ["SB", "SJ", "SR"].includes(bill?.billDetails?.billType?.code)));

         const carrier = showCarrier
         ? `Carrier:\n${bill.billDetails.carrierInfo.lastName}, ${bill.billDetails.carrierInfo.firstName.charAt(0)}.`
         : "";

      // Use the processVotes function to get vote details
      const voteDetails = processVotes(bill.billVoteDetails);

      return [
        `${index + 1})`,
        `${bill.billDetails?.billType?.code} ${bill.billDetails?.billNumber}`,
        sponsor,
        carrier,
        bill.comment?.replace(/<\/?p>/g, "") || "",
        `${bill?.billDetails?.draft?.shortTitle || "N/A"}\n${voteDetails}`
      ];
    });    

    doc.autoTable({
      body: tableData,
      startY: paddingBeforeTable,
      styles: {
        fontSize: 9,
        textColor: [0, 0, 0],
      },
      tableWidth: "wrap",
      columnStyles: {
        0: {
          cellWidth:
            0.03 * (doc.internal.pageSize.width - leftMargin - rightMargin),
        },
        1: {
          cellWidth:
            0.07 * (doc.internal.pageSize.width - leftMargin - rightMargin),
        },
        2: {
          cellWidth:
            0.1 * (doc.internal.pageSize.width - leftMargin - rightMargin),
        },
        3: {
          cellWidth:
            0.1 * (doc.internal.pageSize.width - leftMargin - rightMargin),
        },
        4: {
          cellWidth:
            0.15 * (doc.internal.pageSize.width - leftMargin - rightMargin),
        },
        5: {
          cellWidth:
            0.55 * (doc.internal.pageSize.width - leftMargin - rightMargin),
        },
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      didParseCell: (data) => {
        if (data.column.index === 1) {
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    paddingBeforeTable = doc.lastAutoTable.finalY + 10;
  });

  const suffixesUsed = new Set();
  selectedAgenda.agendaBills.forEach((bill) => {
    const billSuffix = bill.suffix;
    if (billSuffix && suffixLegend.some(([suffix]) => suffix === billSuffix)) {
      suffixesUsed.add(billSuffix);
    }
  });

  if (suffixesUsed.size > 0) {
    doc.setFontSize(7);
    doc.setTextColor(0, 0, 0);

    const suffixLegend = [
      ["CC", "Conference Committee"],
      ["FC", "Free Conference Committee"],
      ["GA", "Governor's Proposed Amendments"],
      ["HA", "House Amendments"],
      ["SA", "Senate Amendments"],
      ["@", "2nd Reading/Finance Committee"],
      [
        "^",
        "Referred to and returned from the Appropriations or Finance & Claims Committees w/o amendments",
      ],
      ["*", "Amended on second reading"],
    ];

    let legendYPosition = doc.internal.pageSize.height - 50;
    suffixLegend.forEach(([suffix, description], index) => {
      if (suffixesUsed.has(suffix)) {
        doc.text(`${suffix} - ${description}`, 45, legendYPosition);
        legendYPosition += 10;
      }
    });
  }

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addHeader(doc, i, totalPages);
  }

  const calendarDate = new Date(dateText);
  const formattedDate = `${(calendarDate.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${calendarDate
    .getDate()
    .toString()
    .padStart(2, "0")}-${calendarDate.getFullYear()}`;
  const formattedFileName = `${formattedDate}-${selectedAgenda.chamber}Agenda${selectedAgenda.agendaNumber}.pdf`;

  doc.save(formattedFileName);
};

export default printFloorAgendaPDF;

